import * as React from 'react';
import * as styles from './login.module.scss';
import { Button } from '@material-ui/core';
import OutlinedInput from '../common/outlined-input';
import logo from '../../images/logo.png';
import useLogin from '../../hooks/useLogin';
import {
  isValidPassword,
  containsLowercaseCharacters,
  containsUppercaseCharacters,
  containsDigits,
  containsSpecialCharacters
} from '../../utilities/utilities';
import { RouteComponentProps, withRouter, Redirect } from 'react-router-dom';
import Paths from '../../models/enums/paths';
import FailureAnnouncement from '../announcement/failure-announcement';

interface MatchParams {
  clientId: string;
  resetToken: string;
}

/**
 * ChangePassword
 * @function ChangePassword - Component for the change password form
 */
const ChangePassword = (props: RouteComponentProps<MatchParams>) => {
  const [oldPassword, setOldPassword] = React.useState<string>('');
  const [newPassword, setNewPassword] = React.useState<string>('');
  const [confirmPassword, setConfirmPassword] = React.useState<string>('');
  const [oldPasswordError, setOldPasswordError] = React.useState<boolean>(false);
  const [newPasswordError, setNewPasswordError] = React.useState<boolean>(false);
  const [confirmPasswordError, setConfirmPasswordError] = React.useState<boolean>(false);
  const [resetSuccess, setResetSuccess] = React.useState<boolean>(false);
  const [resetErrorMessage, setResetErrorMessage] = React.useState<string>(null);
  const { changePassword } = useLogin();

  /** @const validPassword checks to see if the password is a valid password */
  const validPassword = isValidPassword(newPassword);
  /** @const validConfirmPassword checks to see if the confirm password is a valid password */
  const validConfirmPassword = isValidPassword(confirmPassword);
  /** @const passwordsMatch Confirms that the new password and the confirmed password match */
  const passwordsMatch = confirmPassword === newPassword;
  /** @const disabled - Update Password button is disabled if passwords don't match and password doesn't meet minimum requirements */
  const disabled = !(passwordsMatch && validPassword);
  /** @const clientId - The ID of the client who's password will be updated */
  const clientId = props.match.params.clientId;

  /**
   * @function updatePassword Updates the password in the database
   */
  const updatePassword = () => {
    changePassword(clientId, oldPassword, newPassword)
      .then(() => {
        setResetSuccess(true);
      })
      .catch(err => {
        setResetErrorMessage(err.responseJSON.message);
      });
  };

  /**
   * @function displayErrorMessage Displays an error message
   * @param password Password to check to display error message
   */
  const displayErrorMessage = (password: string) => {
    if (password === confirmPassword && confirmPassword !== newPassword) {
      return 'Passwords do not match';
    } else if (password.length === 0) {
      return 'Please enter password';
    } else if (password.length > 0 && password.length < 8) {
      return 'Password must be at least 8 characters';
    } else if (!containsLowercaseCharacters(password)) {
      return 'Password must contain at least 1 lowercase character';
    } else if (!containsUppercaseCharacters(password)) {
      return 'Password must contain at least 1 uppercase character';
    } else if (!containsDigits(password)) {
      return 'Password must contain at least 1 digit';
    } else if (!containsSpecialCharacters(password)) {
      return 'Must contain at least 1 special character (!, @, #, $, %, ^)';
    }
    return '';
  };

  if (resetSuccess) {
    return <Redirect to={{ pathname: Paths.Login, state: { loginSuccessful: true } }} />;
  }

  return (
    <div>
      {resetErrorMessage && <FailureAnnouncement message={resetErrorMessage} />}
      <div className={styles.loginForm}>
        <div className={styles.loginBox}>
          <img src={logo} />
          <OutlinedInput
            error={oldPassword.length > 0 && !validPassword}
            id="outlined-old-password"
            type="password"
            label="Old Password"
            margin="normal"
            variant="outlined"
            value={oldPassword}
            onChange={(event: any) => setOldPassword(event.target.value)}
            onBlur={() => setOldPasswordError(!validPassword)}
          />
          {oldPasswordError && <span className={styles.errorMessage}>{displayErrorMessage(oldPassword)}</span>}
          <OutlinedInput
            error={
              (newPassword.length > 0 && !validPassword) ||
              (confirmPassword.length > 0 && (!validPassword || !passwordsMatch))
            }
            id="outlined-password"
            type="password"
            label="New Password"
            margin="normal"
            variant="outlined"
            value={newPassword}
            onChange={(event: any) => setNewPassword(event.target.value)}
            onBlur={() => setNewPasswordError(!validPassword)}
          />
          {newPasswordError && <span className={styles.errorMessage}>{displayErrorMessage(newPassword)}</span>}
          <OutlinedInput
            error={confirmPassword.length > 0 && (!validPassword || !passwordsMatch)}
            id="outlined-confirm"
            type="password"
            label="Confirm Password"
            margin="normal"
            variant="outlined"
            value={confirmPassword}
            onChange={(event: any) => setConfirmPassword(event.target.value)}
            onBlur={() => setConfirmPasswordError(!validConfirmPassword)}
          />
          {confirmPasswordError && <span className={styles.errorMessage}>{displayErrorMessage(confirmPassword)}</span>}
          <div className={styles.buttonContainer}>
            <Button
              disabled={disabled}
              variant="contained"
              color="primary"
              className={styles.loginButton}
              classes={{ disabled: styles.disabledButton }}
              onClick={() => updatePassword()}
            >
              Change Password
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(ChangePassword);
